const API_BASE = import.meta.env.VITE_API_URL || '/api';

export interface User {
  id: number;
  username: string;
  telegram_id: string | null;
  balance: number;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user_id: number;
  nickname: string;
  balance: number;
  best_distance: number;
}

export interface ProfileResponse {
  user_id: number;
  nickname: string;
  email: string;
  balance: number;
  best_distance: number;
}

export interface RoomResponse {
  id: number;
  code: string;
  host_id: number;
  room_type: string;
  max_players: number;
  winner_bonus: number;
  status: string;
  players: RoomPlayerResponse[];
}

// Response from create/join/findMatch - has room_id instead of id
export interface RoomCreateResponse {
  room_id: number;
  code: string;
  room_type: string;
  status: string;
}

export interface RoomJoinResponse {
  room_id: number;
  code: string;
  status: string;
  players: RoomPlayerResponse[];
  your_color: string;
}

export interface MatchmakingResponse {
  room_id: number;
  code: string;
  players_count: number;
  max_players: number;
  status: string;
  your_color: string;
  message: string | null;
}

export interface RoomPlayerResponse {
  user_id: number;
  nickname: string;
  color: string;
  is_ready: boolean;
  is_alive: boolean;
  final_distance: number;
  payout: number;
}

export interface UserSettings {
  sound_enabled: boolean;
  sound_volume: number;
  preferred_plane_color: string;
}

// ========== Tuning Types ==========

export interface GunType {
  id: number;
  name: string;
  price: number;
  magazine_size: number;
  fire_mode: 'single' | 'burst_5' | 'burst_10' | 'burst_15';
  burst_count: number;
  fire_rate: number;
}

export interface ArmorPrice {
  id: number;
  hits: number;
  price: number;
}

export interface AmmoPrice {
  id: number;
  amount: number;
  price: number;
}

export interface BoostPrice {
  id: number;
  amount: number;
  price: number;
}

export interface TuningShop {
  guns: GunType[];
  armor_prices: ArmorPrice[];
  ammo_prices: AmmoPrice[];
  boost_prices: BoostPrice[];
}

export interface PlayerTuning {
  gun: GunType | null;
  ammo: number;
  armor: number;
  boost: number;
  balance: number;
}

export interface PurchaseResponse {
  success: boolean;
  message: string;
  new_balance: number;
  gun: GunType | null;
  ammo: number;
  armor: number;
  boost: number;
}

// ========== Payment Types ==========

export interface PaymentCreate {
  amount_rub: number;
}

export interface PaymentResponse {
  id: number;
  status: string;
  amount_rub: number;
  amount_fly: number;
  payment_url: string | null;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
}

export interface PaymentHistoryItem {
  id: number;
  status: string;
  amount_rub: number;
  amount_fly: number;
  created_at: string;
  completed_at: string | null;
}

export interface GameObject {
  type: 'bonus' | 'multiplier' | 'missile' | 'fuel_canister' | 'turret' | 'enemy_plane';
  x: number;
  y: number;
  value: number;
  // Optional fields for turrets and enemy planes
  angle?: number;  // Turret: угол стрельбы
  fire_rate?: number;  // Turret/Enemy: интервал стрельбы
  direction?: number;  // Enemy plane: направление (-1 или 1)
  speed?: number;  // Enemy plane: скорость
}

export interface RoundData {
  id: number;
  seed: string;
  objects_map: GameObject[];
  status: string;
}

export interface RoundResult {
  id: number;
  distance: number;
  max_height: number;
  final_multiplier: number;
  bonuses_collected: number;
  payout: number;
  status: string;
  new_balance: number;
}

class ApiClient {
  private userId: number | null = null;
  private token: string | null = null;
  private nickname: string | null = null;

  constructor() {
    // Load from localStorage
    this.token = localStorage.getItem('flyformoney_token');
    const userId = localStorage.getItem('flyformoney_user_id');
    this.userId = userId ? parseInt(userId) : null;
    this.nickname = localStorage.getItem('flyformoney_nickname');
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async register(email: string, password: string, nickname: string): Promise<AuthResponse> {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, nickname })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Registration failed');
    }
    const data: AuthResponse = await res.json();
    this.saveAuth(data);
    return data;
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Login failed');
    }
    const data: AuthResponse = await res.json();
    this.saveAuth(data);
    return data;
  }

  async checkAuth(): Promise<AuthResponse | null> {
    if (!this.token) return null;
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: this.getHeaders()
      });
      if (!res.ok) {
        // Only logout on 401 (invalid token), not on network/server errors
        if (res.status === 401) {
          this.logout();
        }
        return null;
      }
      const data: AuthResponse = await res.json();
      this.saveAuth(data);
      return data;
    } catch {
      // Network error - don't clear token, backend might just be down
      return null;
    }
  }

  private saveAuth(data: AuthResponse) {
    this.token = data.access_token;
    this.userId = data.user_id;
    this.nickname = data.nickname;
    localStorage.setItem('flyformoney_token', data.access_token);
    localStorage.setItem('flyformoney_user_id', data.user_id.toString());
    localStorage.setItem('flyformoney_nickname', data.nickname);
  }

  logout() {
    this.token = null;
    this.userId = null;
    this.nickname = null;
    localStorage.removeItem('flyformoney_token');
    localStorage.removeItem('flyformoney_user_id');
    localStorage.removeItem('flyformoney_nickname');
  }

  isAuthenticated(): boolean {
    return this.token !== null && this.userId !== null;
  }

  getNickname(): string | null {
    return this.nickname;
  }

  async getProfile(): Promise<ProfileResponse> {
    const res = await fetch(`${API_BASE}/auth/profile`, {
      headers: this.getHeaders()
    });
    if (!res.ok) throw new Error('Failed to get profile');
    return res.json();
  }

  async updateProfile(nickname: string): Promise<ProfileResponse> {
    const res = await fetch(`${API_BASE}/auth/profile`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ nickname })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to update profile');
    }
    const data = await res.json();
    this.nickname = data.nickname;
    localStorage.setItem('flyformoney_nickname', data.nickname);
    return data;
  }

  // Legacy methods for backward compatibility
  async createUser(username: string): Promise<User> {
    const res = await fetch(`${API_BASE}/users/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username })
    });
    if (!res.ok) throw new Error('Failed to create user');
    const user = await res.json();
    this.userId = user.id;
    return user;
  }

  async getUser(userId: number): Promise<User> {
    const res = await fetch(`${API_BASE}/users/${userId}`);
    if (!res.ok) throw new Error('User not found');
    return res.json();
  }

  async getBalance(): Promise<number> {
    if (!this.userId) throw new Error('User not initialized');
    const res = await fetch(`${API_BASE}/users/${this.userId}/balance`);
    if (!res.ok) throw new Error('Failed to get balance');
    const data = await res.json();
    return data.balance;
  }

  async abandonActiveGames(): Promise<void> {
    const res = await fetch(`${API_BASE}/games/abandon`, {
      method: 'POST',
      headers: this.getHeaders()
    });
    if (!res.ok) {
      console.warn('Failed to abandon active games');
    }
  }

  async startRound(): Promise<RoundData> {
    const res = await fetch(`${API_BASE}/games/start`, {
      method: 'POST',
      headers: this.getHeaders()
    });
    if (!res.ok) {
      const err = await res.json();

      // If there's an active game, try to abandon it and retry
      if (res.status === 400 && err.detail?.includes('already have an active game')) {
        await this.abandonActiveGames();
        // Retry start
        const retryRes = await fetch(`${API_BASE}/games/start`, {
          method: 'POST',
          headers: this.getHeaders()
        });
        if (!retryRes.ok) {
          throw new Error('Failed to start round after abandoning previous game');
        }
        return retryRes.json();
      }

      throw new Error(err.detail || 'Failed to start round');
    }
    return res.json();
  }

  async completeRound(
    roundId: number,
    collectedIndices: number[],
    distance: number,
    crashed: boolean,
    ammoSpent: number = 0,
    armorUsed: number = 0
  ): Promise<RoundResult> {
    const params = [
      ...collectedIndices.map(i => `collected_indices=${i}`),
      `distance=${distance}`,
      `crashed=${crashed}`,
      `ammo_spent=${ammoSpent}`,
      `armor_used=${armorUsed}`
    ].join('&');
    const res = await fetch(
      `${API_BASE}/games/rounds/${roundId}/complete?${params}`,
      {
        method: 'POST',
        headers: this.getHeaders()
      }
    );
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Complete round failed:', res.status, errorText);
      throw new Error(`Failed to complete round: ${res.status} - ${errorText}`);
    }
    return res.json();
  }

  async refuel(fuelAmount: number): Promise<{ fuel_purchased: number; cost: number; new_balance: number }> {
    const res = await fetch(
      `${API_BASE}/games/refuel?fuel_amount=${fuelAmount}`,
      {
        method: 'POST',
        headers: this.getHeaders()
      }
    );
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to refuel');
    }
    return res.json();
  }

  async getLeaderboard(): Promise<{
    top_distance: { nickname: string; value: number }[];
    top_balance: { nickname: string; value: number }[];
    user_rank_distance: number | null;
    user_rank_balance: number | null;
  }> {
    const params = this.userId ? `?user_id=${this.userId}` : '';
    const res = await fetch(`${API_BASE}/games/stats/leaderboard${params}`);
    if (!res.ok) throw new Error('Failed to get leaderboard');
    return res.json();
  }

  setUserId(id: number) {
    this.userId = id;
  }

  getUserId(): number | null {
    return this.userId;
  }

  getToken(): string | null {
    return this.token;
  }

  // ========== Room API ==========

  async createRoom(roomType: 'private' | 'public', maxPlayers: number = 5): Promise<RoomCreateResponse> {
    const res = await fetch(`${API_BASE}/rooms/create`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ room_type: roomType, max_players: maxPlayers })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Не удалось создать сервер');
    }
    return res.json();
  }

  async findMatch(): Promise<MatchmakingResponse> {
    const res = await fetch(`${API_BASE}/rooms/find-match`, {
      method: 'POST',
      headers: this.getHeaders()
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Не удалось найти игру');
    }
    return res.json();
  }

  async joinRoom(code: string): Promise<RoomJoinResponse> {
    const res = await fetch(`${API_BASE}/rooms/join/${code}`, {
      method: 'POST',
      headers: this.getHeaders()
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Не удалось подключиться');
    }
    return res.json();
  }

  async getRoom(roomId: number): Promise<RoomResponse> {
    const res = await fetch(`${API_BASE}/rooms/${roomId}`, {
      headers: this.getHeaders()
    });
    if (!res.ok) {
      throw new Error('Room not found');
    }
    return res.json();
  }

  async leaveRoom(roomId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/rooms/${roomId}/leave`, {
      method: 'POST',
      headers: this.getHeaders()
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to leave room');
    }
  }

  // ========== Settings API ==========

  async getSettings(): Promise<UserSettings> {
    const res = await fetch(`${API_BASE}/auth/settings`, {
      headers: this.getHeaders()
    });
    if (!res.ok) {
      // Return defaults if endpoint doesn't exist
      return {
        sound_enabled: true,
        sound_volume: 0.5,
        preferred_plane_color: 'red'
      };
    }
    return res.json();
  }

  async updateSettings(settings: Partial<UserSettings>): Promise<UserSettings> {
    const res = await fetch(`${API_BASE}/auth/settings`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(settings)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to update settings');
    }
    return res.json();
  }

  // ========== Tuning API ==========

  async getTuningShop(): Promise<TuningShop> {
    const res = await fetch(`${API_BASE}/tuning/shop`, {
      headers: this.getHeaders()
    });
    if (!res.ok) throw new Error('Failed to get tuning shop');
    return res.json();
  }

  async getMyTuning(): Promise<PlayerTuning> {
    const res = await fetch(`${API_BASE}/tuning/my`, {
      headers: this.getHeaders()
    });
    if (!res.ok) throw new Error('Failed to get tuning');
    return res.json();
  }

  async buyGun(gunTypeId: number): Promise<PurchaseResponse> {
    const res = await fetch(`${API_BASE}/tuning/buy/gun`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ gun_type_id: gunTypeId })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to buy gun');
    }
    return res.json();
  }

  async buyArmor(hits: number): Promise<PurchaseResponse> {
    const res = await fetch(`${API_BASE}/tuning/buy/armor`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ hits })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to buy armor');
    }
    return res.json();
  }

  async buyAmmo(amount: number): Promise<PurchaseResponse> {
    const res = await fetch(`${API_BASE}/tuning/buy/ammo`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ amount })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to buy ammo');
    }
    return res.json();
  }

  async buyBoost(amount: number): Promise<PurchaseResponse> {
    const res = await fetch(`${API_BASE}/tuning/buy/boost`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ amount })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to buy boost');
    }
    return res.json();
  }

  // ========== Payment API ==========

  async createPayment(amountRub: number): Promise<PaymentResponse> {
    const res = await fetch(`${API_BASE}/payments/create`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ amount_rub: amountRub })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to create payment');
    }
    return res.json();
  }

  async getPaymentStatus(paymentId: number): Promise<PaymentResponse> {
    const res = await fetch(`${API_BASE}/payments/${paymentId}/status`, {
      headers: this.getHeaders()
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to get payment status');
    }
    return res.json();
  }

  async cancelPayment(paymentId: number): Promise<{ success: boolean; message: string }> {
    const res = await fetch(`${API_BASE}/payments/${paymentId}/cancel`, {
      method: 'POST',
      headers: this.getHeaders()
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to cancel payment');
    }
    return res.json();
  }

  async getPaymentHistory(limit: number = 10): Promise<PaymentHistoryItem[]> {
    const res = await fetch(`${API_BASE}/payments/history?limit=${limit}`, {
      headers: this.getHeaders()
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to get payment history');
    }
    return res.json();
  }
}

export const apiClient = new ApiClient();
